import { assign, defineMachine } from 'fsm';
import { lowLevelAlertErrorTypes } from './LowLevelAuthErrorAlert';
export function getLowLevelAuthFlowMachineDefinition() {
  return defineMachine()({
    initial: 'flow',
    context: {},
    states: {
      flow: {
        on: {
          GO_TO_NEXT_STEP: {
            target: 'flow',
            cond: 'canGoToNextStep',
            actions: ['goToNextStep']
          },
          GO_TO_PREV_STEP: {
            target: 'flow',
            cond: 'canGoToPrevStep',
            actions: ['goToPrevStep']
          },
          UPDATE_PARAM: {
            actions: ['updateParam']
          },
          START_AUTH: {
            target: 'authorizing',
            actions: ['startAuthProcess']
          },
          FINISH_FAILURE: [{
            cond: 'isCustomError',
            target: 'customError',
            actions: ['setAuthError']
          }, {
            target: 'flow',
            actions: ['setAuthError']
          }],
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'reportSuccess']
          },
          CLEAR_ERROR: {
            actions: ['clearAuthError']
          }
        }
      },
      authorizing: {
        on: {
          OAUTH_WINDOW_OPENED: {
            actions: ['setOAuthWindow']
          },
          OAUTH_WINDOW_BLOCKED: {
            target: 'flow',
            actions: ['setAuthFailed', 'setOAuthWindowBlockedError']
          },
          OAUTH_WINDOW_TIMEOUT: {
            target: 'flow',
            actions: ['setAuthCancelled', 'setOAuthTimeoutError', 'closeOAuthWindow']
          },
          OAUTH_WINDOW_CLOSED: {
            target: 'flow',
            actions: ['cancelAuthAsWindowClosed']
          },
          START_POLLING: {
            target: 'polling',
            actions: ['setPollingStatus']
          },
          OAUTH_RESPONSE_RECEIVED: [{
            cond: 'isValidOAuthState',
            target: 'authenticating',
            actions: ['setAuthenticatingStatus']
          }, {
            target: 'flow',
            actions: ['setAuthFailed', 'setUnknownOAuthError']
          }],
          FINISH_FAILURE: [{
            cond: 'isCustomError',
            target: 'customError',
            actions: ['setAuthError', 'resetAuthProcess']
          }, {
            target: 'flow',
            actions: ['setAuthError', 'resetAuthProcess']
          }],
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'reportSuccess', 'resetAuthProcess']
          }
        }
      },
      polling: {
        on: {
          POLL_TICK: {
            actions: ['incrementPollingCount']
          },
          POLL_SUCCESS: [{
            cond: 'isPollResponseReady',
            target: 'authenticating',
            actions: ['setAuthenticatingStatus']
          }, {
            target: 'polling'
          }],
          POLL_ERROR: {
            target: 'flow',
            actions: ['setAuthFailed', 'setUnknownOAuthError']
          },
          OAUTH_RESPONSE_RECEIVED: [{
            cond: 'isValidOAuthState',
            target: 'authenticating',
            actions: ['setAuthenticatingStatus']
          }, {
            target: 'flow',
            actions: ['setAuthFailed', 'setUnknownOAuthError']
          }],
          OAUTH_WINDOW_TIMEOUT: {
            target: 'flow',
            actions: ['setAuthCancelled', 'setOAuthTimeoutError', 'closeOAuthWindow']
          },
          OAUTH_WINDOW_CLOSED: {
            target: 'pollingAfterClose',
            actions: ['setPollingAfterCloseStatus']
          },
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'reportSuccess', 'resetAuthProcess']
          },
          FINISH_FAILURE: [{
            cond: 'isCustomError',
            target: 'customError',
            actions: ['setAuthError', 'resetAuthProcess']
          }, {
            target: 'flow',
            actions: ['setAuthError', 'resetAuthProcess']
          }]
        }
      },
      // Grace-period state entered when the OAuth popup closes during `polling`.
      // The oauth-redirect page updates the backend session and then calls
      // window.close() — the parent's heartbeat can observe the close before
      // the next poll tick lands, so transitioning straight to `OAuthWindowClosed`
      // would race successful auth. We keep polling briefly (bounded by
      // POLLING_AFTER_CLOSE_TIMEOUT) so an in-flight AUTH_RESPONSE can still win.
      pollingAfterClose: {
        on: {
          POLL_TICK: {
            actions: ['incrementPollingCount']
          },
          POLL_SUCCESS: [{
            cond: 'isPollResponseReady',
            target: 'authenticating',
            actions: ['setAuthenticatingStatus']
          }, {
            target: 'flow',
            actions: ['cancelAuthAsWindowClosed']
          }],
          // A grace-period poll failure is reported as OAuthWindowClosed
          // (not UnknownOAuthProblem) because the user's explicit action —
          // closing the popup — is the primary signal; a transient network
          // error here shouldn't eclipse that in the user-facing message.
          POLL_ERROR: {
            target: 'flow',
            actions: ['cancelAuthAsWindowClosed']
          },
          POLLING_AFTER_CLOSE_TIMEOUT: {
            target: 'flow',
            actions: ['cancelAuthAsWindowClosed']
          },
          OAUTH_WINDOW_TIMEOUT: {
            target: 'flow',
            actions: ['setAuthCancelled', 'setOAuthTimeoutError', 'closeOAuthWindow']
          },
          OAUTH_WINDOW_CLOSED: {},
          OAUTH_RESPONSE_RECEIVED: [{
            cond: 'isValidOAuthState',
            target: 'authenticating',
            actions: ['setAuthenticatingStatus']
          }, {
            target: 'flow',
            actions: ['setAuthFailed', 'setUnknownOAuthError']
          }],
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'reportSuccess', 'resetAuthProcess']
          },
          FINISH_FAILURE: [{
            cond: 'isCustomError',
            target: 'customError',
            actions: ['setAuthError', 'resetAuthProcess']
          }, {
            target: 'flow',
            actions: ['setAuthError', 'resetAuthProcess']
          }]
        }
      },
      authenticating: {
        on: {
          // Ignore polling events while authenticating - polling may still be running
          POLL_TICK: {},
          POLL_SUCCESS: {},
          POLL_ERROR: {},
          // Ignore OAUTH_RESPONSE_RECEIVED - for polling flow, POLL_SUCCESS already transitioned here
          OAUTH_RESPONSE_RECEIVED: {},
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'reportSuccess', 'resetAuthProcess']
          },
          FINISH_FAILURE: [{
            cond: 'isCustomError',
            target: 'customError',
            actions: ['setAuthError', 'resetAuthProcess']
          }, {
            target: 'flow',
            actions: ['setAuthError', 'resetAuthProcess']
          }]
        }
      },
      customError: {
        on: {
          RESET_CUSTOM_ERROR: {
            target: 'flow',
            actions: ['resetAfterCustomError']
          }
        }
      },
      success: {
        on: {
          RESET_FLOW: {
            target: 'flow',
            actions: ['resetFlowState']
          }
        }
      }
    },
    actions: {
      goToNextStep: assign(context => ({
        stepIndex: Math.min(context.stepIndex + 1, context.authConfig.authFlow.steps.length - 1)
      })),
      goToPrevStep: assign(context => ({
        stepIndex: Math.max(context.stepIndex - 1, context.initialStepIndex)
      })),
      updateParam: assign((context, event) => {
        if (event.type !== 'UPDATE_PARAM') {
          return {};
        }
        return {
          params: Object.assign({}, context.params, {
            [event.name]: event.value
          })
        };
      }),
      startAuthProcess: assign(() => ({
        authProcessStatus: 'authorizing',
        pollingCount: 0,
        authError: undefined
      })),
      setOAuthWindow: assign((_context, event) => {
        if (event.type !== 'OAUTH_WINDOW_OPENED') {
          return {};
        }
        return {
          oAuthWindow: event.window,
          oAuthState: event.oAuthState
        };
      }),
      setPollingStatus: assign(() => ({
        authProcessStatus: 'polling'
      })),
      setPollingAfterCloseStatus: assign(() => ({
        authProcessStatus: 'pollingAfterClose'
      })),
      setAuthenticatingStatus: assign(() => ({
        authProcessStatus: 'authenticating'
      })),
      incrementPollingCount: assign(context => ({
        pollingCount: context.pollingCount + 1
      })),
      setAuthFailed: assign(() => ({
        authProcessStatus: 'failed'
      })),
      setAuthCancelled: assign(() => ({
        authProcessStatus: 'cancelled'
      })),
      setOAuthWindowBlockedError: assign(() => ({
        authError: {
          type: 'OAuthWindowBlocked'
        }
      })),
      setOAuthTimeoutError: assign(() => ({
        authError: {
          type: 'OAuthWindowTimeout'
        }
      })),
      setUnknownOAuthError: assign(() => ({
        authError: {
          type: 'UnknownOAuthProblem'
        }
      })),
      closeOAuthWindow: context => {
        if (context.oAuthWindow) {
          context.oAuthWindow.close();
        }
      },
      cancelAuthAsWindowClosed: assign(() => ({
        authProcessStatus: 'cancelled',
        authError: {
          type: 'OAuthWindowClosed'
        },
        oAuthWindow: null,
        oAuthState: ''
      })),
      resetAuthProcess: assign(() => ({
        authProcessStatus: 'idle',
        pollingCount: 0,
        oAuthWindow: null,
        oAuthState: ''
      })),
      setAuthSucceeded: assign((_context, event) => {
        if (event.type !== 'FINISH_SUCCESS') {
          return {};
        }
        return {
          authState: {
            status: 'succeeded',
            authResponse: event.response
          },
          authError: undefined,
          authProcessStatus: 'idle'
        };
      }),
      setAuthError: assign((_context, event) => {
        if (event.type !== 'FINISH_FAILURE') {
          return {};
        }
        return {
          authError: event.error,
          authProcessStatus: 'failed'
        };
      }),
      reportSuccess: (context, event) => {
        if (event.type === 'FINISH_SUCCESS') {
          context.onComplete(event.response);
        }
      },
      resetAfterCustomError: assign(context => ({
        authError: undefined,
        stepIndex: context.initialStepIndex,
        authProcessStatus: 'idle',
        pollingCount: 0,
        oAuthWindow: null,
        oAuthState: ''
      })),
      resetFlowState: assign(context => ({
        authError: undefined,
        authState: {
          status: 'pending'
        },
        stepIndex: context.initialStepIndex,
        authProcessStatus: 'idle',
        pollingCount: 0,
        oAuthWindow: null,
        oAuthState: ''
      })),
      clearAuthError: assign(() => ({
        authError: undefined
      }))
    },
    guards: {
      canGoToNextStep: context => {
        const {
          inline = false,
          steps
        } = context.authConfig.authFlow;
        return !inline && context.stepIndex < steps.length - 1;
      },
      canGoToPrevStep: context => {
        const {
          inline = false
        } = context.authConfig.authFlow;
        return !inline && context.stepIndex > context.initialStepIndex;
      },
      isCustomError: (_context, event) => event.type === 'FINISH_FAILURE' && !lowLevelAlertErrorTypes.includes(event.error.type),
      isValidOAuthState: (context, event) => {
        if (event.type !== 'OAUTH_RESPONSE_RECEIVED') {
          return false;
        }
        return event.state === context.oAuthState;
      },
      isPollResponseReady: (_context, event) => {
        if (event.type !== 'POLL_SUCCESS') {
          return false;
        }
        return event.status !== 'PENDING';
      }
    }
  });
}